// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useRef, useState } from 'react';
import { Label } from 'components/atoms/Label';
import { ErrorText } from 'components/atoms/ErrorText';
import useOutsideClick from 'util/hooks/useClickOutside';
import styles from './SelectFieldMultipleChoise.module.scss';
import { IconDropDown } from 'components/atoms/icons/IconDropDown';
import Tag from 'components/atoms/Tag';
import { CheckBoxCardV2 } from '../CheckBoxCardV2';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from 'util/commons';
import _uniqBy from 'lodash/uniqBy';
import { IconClose } from 'components/atoms/icons/IconClose';
import Portal from 'components/atoms/Portal';
import { useGetBoundingClientRect } from 'util/hooks/useGetBoundingClientRect';

export type OptionItem = {
  id: string | number;
  name: string;
  [key: string]: string | number;
};

interface Props {
  name: string;
  selectedOptions: OptionItem[] | (string | number)[];
  options: OptionItem[];
  searchable?: boolean;
  onChange: (tags: OptionItem[]) => void;
  className?: string;
  label?: string;
  required?: boolean;
  placeholder?: string | null;
  disabled?: boolean;
  maxTagError?: string;
  checkAllWithCheckBox?: boolean;
  darkMode?: boolean;
  allowClear?: boolean;
  usePortal?: boolean;
  menuPlacement?: 'top' | 'bottom';
  fontSize?: 'medium' | 'large';
}

export function SelectFieldMultipleChoise({
  label,
  selectedOptions: selectedOptionProps,
  placeholder,
  options,
  searchable,
  disabled,
  className,
  onChange,
  required,
  maxTagError,
  checkAllWithCheckBox,
  darkMode,
  allowClear,
  usePortal,
  menuPlacement,
  fontSize,
}: Props) {
  const [isOpenOptions, setIsOpenOptions] = useState(false);
  const [checkError, setCheckError] = useState(false);
  const errorRef = useRef(false);
  const [checkAll, setCheckAll] = useState(false);
  const divRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<OptionItem[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: dropDownRef } = useOutsideClick({
    onClickOutside: () => {
      setIsOpenOptions(false);
    },
    exceptRefs: [containerRef],
  });

  const { domRect } = useGetBoundingClientRect(
    {
      el: containerRef.current,
      trackScroll: usePortal && isOpenOptions,
    },
    [isOpenOptions]
  );

  useEffect(() => {
    if (!usePortal || !isOpenOptions || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setIsOpenOptions(false);
        }
      },
      { threshold: 0 }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [usePortal, isOpenOptions]);

  const optionMapper = options.reduce(
    (rs: Record<string, string>, { id, name }) => {
      rs[id] = name;

      return rs;
    },
    {}
  );

  const selectedOptions: OptionItem[] =
    typeof selectedOptionProps[0] !== 'object'
      ? (selectedOptionProps as (string | number)[]).reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (rs: OptionItem[], val: any) => {
            const label = optionMapper[val];

            if (label) {
              rs.push({
                id: val,
                name: label,
              });
            }

            return rs;
          },
          []
        )
      : (selectedOptionProps as OptionItem[]);

  useEffect(() => {
    if (!errorRef.current && isOpenOptions) {
      errorRef.current = true;
    }
    if (
      errorRef.current &&
      !isOpenOptions &&
      required &&
      !disabled &&
      !selectedOptions.length
    ) {
      setCheckError(true);
    } else setCheckError(false);
  }, [isOpenOptions, selectedOptions]);

  useEffect(() => {
    setCheckAll(
      selectedOptions.length > 0 &&
        filterOptions.every((option) =>
          selectedOptions.find(
            (item) => option.id.toString() === item.id.toString()
          )
        )
    );
  }, [selectedOptions.length, filterOptions.length]);

  useEffect(() => {
    setFilterOptions(options);
  }, [isOpenOptions]);

  const handleSelectOption = (option: OptionItem) => {
    const newSelectedOptions = selectedOptions.find(
      (item) => item.id === option.id
    )
      ? selectedOptions.filter((item) => item.id !== option.id)
      : selectedOptions.concat(option);

    setTimeout(() => {
      const inp = (
        usePortal ? containerRef : dropDownRef
      ).current?.getBoundingClientRect();

      divRef.current?.scrollTo({
        left: inp?.right,
        behavior: 'smooth',
      });
    }, 100);
    onChange && onChange(newSelectedOptions);
  };

  const handleOnSearch = (keyword: string) => {
    if (!keyword) {
      setFilterOptions(options);
      return;
    }

    const searchOptions = options.filter((item) => {
      if (item.name.toLowerCase().includes(keyword.toLowerCase())) {
        return item;
      }
    });

    setFilterOptions(searchOptions);
  };

  const removeFilterOptions = useMemo(
    () =>
      selectedOptions.filter(
        (item) => !filterOptions.find((opt) => opt.id === item.id)
      ),
    [selectedOptions, filterOptions]
  );

  const renderOptions = () => {
    if (!isOpenOptions) {
      return null;
    }

    return (
      <ul
        className={mergeClasses({
          [styles.darkModeList]: darkMode,
          [styles.selectOptionsListPortal]: usePortal,
          [styles.menuPlacementTop]: menuPlacement === 'top',
          [styles.optionListLarge]: fontSize === 'large',
        })}
      >
        {searchable && options.length > 0 && (
          <li
            className={mergeClasses({
              [styles.darkModeItem]: darkMode,
            })}
          >
            <div className={styles.searchInput}>
              <input
                placeholder={t('common.placeholder.search') || ''}
                onChange={(e) => handleOnSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </li>
        )}

        {filterOptions.length > 0 &&
          (checkAllWithCheckBox ? (
            <li
              onClick={(e) => {
                e.stopPropagation();
                setCheckAll(!checkAll);

                if (checkAll) {
                  onChange(searchable ? removeFilterOptions : []);
                } else {
                  onChange(
                    _uniqBy([...selectedOptions, ...filterOptions], 'id')
                  );
                  setIsOpenOptions(false);
                }
              }}
              className={darkMode ? styles.darkModeItem : ''}
            >
              <CheckBoxCardV2 checked={checkAll} />
              <span>{t('common.select_all')}</span>
            </li>
          ) : (
            <li
              className={mergeClasses(styles.all, {
                [styles.darkModeItem]: !!darkMode,
              })}
              onClick={(e) => {
                e.stopPropagation();
                setCheckAll(!checkAll);

                if (checkAll) {
                  onChange(searchable ? removeFilterOptions : []);
                } else {
                  onChange(
                    _uniqBy([...selectedOptions, ...filterOptions], 'id')
                  );
                  setIsOpenOptions(false);
                }
              }}
            >
              {checkAll ? t('common.deselect_all') : t('common.select_all')}
            </li>
          ))}

        <>
          {filterOptions.length > 0 ? (
            filterOptions.map((option, i) => {
              return (
                <li
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectOption(option);
                  }}
                  className={darkMode ? styles.darkModeItem : ''}
                >
                  <CheckBoxCardV2
                    checked={
                      !!selectedOptions.find((item) => item.id === option.id)
                    }
                  />
                  <span className={darkMode ? styles.darkModeLabel : ''}>
                    {option.name}
                  </span>
                </li>
              );
            })
          ) : (
            <li
              className={darkMode ? styles.darkModeItem : ''}
              style={{ justifyContent: 'center' }}
            >
              {t('messages.M_008')}
            </li>
          )}
        </>
      </ul>
    );
  };

  const renderOptionsList = () => {
    if (!isOpenOptions) {
      return null;
    }

    if (usePortal && domRect) {
      return (
        <Portal>
          <div
            ref={dropDownRef}
            className={mergeClasses(styles.selectMultipleField, className)}
            style={{ position: 'static' }}
          >
            <div
              style={{
                position: 'absolute',
                top: domRect.bottom + 4,
                left: domRect.left,
                width: domRect.width,
                height: 1,
                zIndex: 100000 - 1,
              }}
            >
              {renderOptions()}
            </div>
          </div>
        </Portal>
      );
    }

    return renderOptions();
  };

  return (
    <div
      ref={usePortal ? containerRef : dropDownRef}
      className={mergeClasses(styles.selectMultipleField, className)}
      onClick={() => {
        if (!disabled) {
          setIsOpenOptions(!isOpenOptions);
        }
      }}
    >
      {label && <Label text={label} tag={'div'} required={required} />}

      <div className={styles.select}>
        <div
          className={mergeClasses(styles.input, {
            [styles.inputDisable]: !!disabled,
            [styles.inputError]:
              !!(required && !selectedOptions.length && checkError) ||
              !!maxTagError,
            [styles.placeHolder]: !selectedOptions.length,
            [styles.darkModeStyle]: !!darkMode,
          })}
        >
          <div ref={divRef} className={styles.wrapSelected}>
            {selectedOptions.length > 0
              ? selectedOptions.map((option) => (
                  <Tag
                    key={option.id}
                    label={option.name}
                    onRemove={(e) => {
                      e.stopPropagation();
                      onChange(
                        selectedOptions.filter((t) => option.id !== t.id)
                      );
                    }}
                  />
                ))
              : placeholder}
          </div>
        </div>

        {renderOptionsList()}

        {allowClear && selectedOptions.length > 0 ? (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              backgroundColor: 'transparent',
              height: '100%',
              width: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
            onClick={(e) => {
              if (disabled) return;

              e.stopPropagation();
              onChange?.([]);
            }}
          >
            <IconClose width={12} height={12} strokeColor="#9EA0A2" />
          </div>
        ) : (
          <IconDropDown />
        )}
      </div>
      {((required && !disabled && checkError) || maxTagError) && (
        <ErrorText
          text={
            maxTagError ||
            t('messages.M_select_required', { field: label }) ||
            ''
          }
          className={styles.error}
        />
      )}
    </div>
  );
}
